import {
  AudioOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileUnknownOutlined,
  FileWordOutlined,
  FileZipOutlined,
  InboxOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useRef, useState } from "react";

import {
  completeUpload,
  createDownloadTicket,
  createPreviewTicket,
  createUploadIntent,
  deleteAttachment,
  fetchCertificateAttachments,
  fetchProjectAttachments,
  renameAttachment,
  uploadToIntent,
  type Attachment,
  type AttachmentTicket,
} from "../../api/attachments";
import { ApiError } from "../../api/client";

const PROJECT_ACCEPT = ".png,.jpg,.webp,.mp3,.mp4,.wav,.rar,.zip,.docx,.doc,.pdf,.xls,.xlsx";
const CERTIFICATE_ACCEPT = ".png,.jpg";
const STATE_LABELS = {
  UPLOADING: { label: "待上传", color: "default" },
  QUARANTINED: { label: "文件校验中", color: "processing" },
  ACTIVE: { label: "可用", color: "success" },
  REJECTED: { label: "已拒绝", color: "error" },
} as const;

function iconFor(extension: string) {
  const props = { "aria-hidden": true };
  if (["png", "jpg", "webp"].includes(extension)) return <FileImageOutlined {...props} />;
  if (extension === "pdf") return <FilePdfOutlined {...props} />;
  if (["doc", "docx"].includes(extension)) return <FileWordOutlined {...props} />;
  if (["xls", "xlsx"].includes(extension)) return <FileExcelOutlined {...props} />;
  if (["zip", "rar"].includes(extension)) return <FileZipOutlined {...props} />;
  if (["mp3", "wav"].includes(extension)) return <AudioOutlined {...props} />;
  if (extension === "mp4") return <VideoCameraOutlined {...props} />;
  return <FileUnknownOutlined {...props} />;
}

function formatSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError
    ? error.message
    : error instanceof Error
      ? error.message
      : fallback;
}

interface AttachmentPanelProps {
  projectId?: string;
  registrantId?: string;
  canUpload: boolean;
  canManage?: boolean;
  attachments?: Attachment[];
}

export function AttachmentPanel({
  projectId,
  registrantId,
  canUpload,
  canManage = true,
  attachments,
}: AttachmentPanelProps) {
  const { message } = App.useApp();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<{
    attachment: Attachment;
    ticket: AttachmentTicket;
  } | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [renaming, setRenaming] = useState<Attachment | null>(null);
  const [nextName, setNextName] = useState("");
  const isCertificate = Boolean(registrantId);
  const queryKey = isCertificate
    ? ["certificate-attachments", registrantId]
    : ["project-attachments", projectId];
  const query = useQuery({
    queryKey,
    queryFn: () =>
      registrantId
        ? fetchCertificateAttachments(registrantId)
        : fetchProjectAttachments(projectId!),
    enabled: attachments === undefined,
    refetchInterval: (result) =>
      result.state.data?.some((item) => ["UPLOADING", "QUARANTINED"].includes(item.state))
        ? 1500
        : false,
  });
  const items = attachments ?? query.data;
  const certificateSlotsUsed = isCertificate
    ? (items?.filter((item) => ["UPLOADING", "QUARANTINED", "ACTIVE"].includes(item.state))
        .length ?? 0)
    : 0;
  const certificateLimitReached = isCertificate && certificateSlotsUsed >= 2;

  const chooseFile = () => inputRef.current?.click();
  const handleFile = async (file: File) => {
    const limit = isCertificate ? 5 * 1024 * 1024 : 100 * 1024 * 1024;
    if (file.size > limit) {
      message.error(`单个附件不能超过 ${isCertificate ? "5" : "100"} MB`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const target = registrantId ? { registrantId } : { projectId: projectId! };
      const intent = await createUploadIntent(target, file);
      await uploadToIntent(intent, file, setProgress);
      await completeUpload(intent.attachment.id);
      message.success("上传完成，正在校验文件");
      await query.refetch();
    } catch (error) {
      message.error(errorMessage(error, "附件上传失败"));
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const view = async (attachment: Attachment) => {
    try {
      const ticket = await createPreviewTicket(attachment.id);
      setPreviewFailed(false);
      setPreviewLoading(true);
      setPreview({ attachment, ticket });
    } catch (error) {
      message.error(errorMessage(error, "预览授权失败"));
    }
  };
  const download = async (attachment: Attachment) => {
    try {
      const ticket = await createDownloadTicket(attachment.id);
      window.location.assign(ticket.url);
    } catch (error) {
      message.error(errorMessage(error, "下载授权失败"));
    }
  };
  const failPreviewLoad = () => {
    setPreviewLoading(false);
    setPreviewFailed(true);
  };
  const saveRename = async () => {
    if (!renaming || !nextName.trim()) return;
    try {
      await renameAttachment(renaming.id, nextName.trim());
      message.success("附件名称已更新");
      setRenaming(null);
      await query.refetch();
    } catch (error) {
      message.error(errorMessage(error, "重命名失败"));
    }
  };
  const remove = async (attachment: Attachment) => {
    try {
      await deleteAttachment(attachment.id);
      message.success("附件已删除；对象保留 30 天供受控运维恢复");
      await query.refetch();
    } catch (error) {
      message.error(errorMessage(error, "附件删除失败"));
    }
  };

  return (
    <section className="attachment-panel" aria-label={isCertificate ? "证件附件" : "项目附件"}>
      <div className="attachment-panel-heading">
        <div>
          <Typography.Title level={5}>{isCertificate ? "证件附件" : "项目附件"}</Typography.Title>
          <Typography.Text type="secondary">
            {isCertificate
              ? `仅 PNG、JPG，单张不超过 5 MB；每个登记人最多 2 张（${certificateSlotsUsed}/2）。`
              : "最多 20 个，每个不超过 100 MB。压缩包仅允许下载。"}
          </Typography.Text>
        </div>
        {canUpload && (
          <>
            <input
              ref={inputRef}
              className="visually-hidden"
              type="file"
              accept={isCertificate ? CERTIFICATE_ACCEPT : PROJECT_ACCEPT}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
            <Button
              type="primary"
              icon={<InboxOutlined />}
              loading={uploading}
              disabled={certificateLimitReached}
              onClick={chooseFile}
            >
              {isCertificate
                ? certificateLimitReached
                  ? "已达 2 张上限"
                  : "上传证件图片"
                : "上传附件"}
            </Button>
          </>
        )}
      </div>
      {uploading && <Progress percent={progress} status="active" size="small" />}
      {attachments === undefined && query.isError && (
        <Alert type="error" showIcon title="附件列表读取失败" />
      )}
      {(attachments !== undefined || !query.isPending) && !items?.length && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无附件" />
      )}
      <div className="attachment-list">
        {items?.map((attachment) => {
          const state = STATE_LABELS[attachment.state];
          return (
            <article className="attachment-row" key={attachment.id}>
              <span className={`file-icon file-icon-${attachment.extension}`}>
                {iconFor(attachment.extension)}
              </span>
              <span className="attachment-identity">
                <strong>{attachment.display_name}</strong>
                <small>
                  {attachment.extension.toUpperCase()} · {formatSize(attachment.size_bytes)}
                </small>
                {attachment.rejection_reason && (
                  <Typography.Text type="danger">{attachment.rejection_reason}</Typography.Text>
                )}
              </span>
              <Tag color={state.color}>{state.label}</Tag>
              <Space size={2} wrap>
                {attachment.state === "ACTIVE" && attachment.can_preview && (
                  <Button type="link" icon={<EyeOutlined />} onClick={() => void view(attachment)}>
                    查看
                  </Button>
                )}
                {attachment.state === "ACTIVE" && (
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => void download(attachment)}
                  >
                    下载
                  </Button>
                )}
                {canManage && attachment.state === "ACTIVE" && (
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setRenaming(attachment);
                      setNextName(attachment.display_name);
                    }}
                  >
                    重命名
                  </Button>
                )}
                {canManage && (
                  <Popconfirm
                    title={`删除 ${attachment.display_name}？`}
                    description="删除后立即停止签发新的查看和下载票据。"
                    okText="删除"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => void remove(attachment)}
                  >
                    <Tooltip title="删除附件">
                      <Button danger type="link" icon={<DeleteOutlined />} aria-label="删除附件" />
                    </Tooltip>
                  </Popconfirm>
                )}
              </Space>
            </article>
          );
        })}
      </div>

      <Modal
        title={preview?.attachment.display_name ?? "附件预览"}
        width="min(1080px, 92vw)"
        open={Boolean(preview)}
        footer={null}
        destroyOnHidden
        onCancel={() => {
          setPreview(null);
          setPreviewLoading(false);
        }}
      >
        <div className="attachment-preview-stage" aria-busy={previewLoading}>
          <Spin spinning={previewLoading} size="large" tip="文件加载中...">
            {previewFailed && (
              <Alert
                type="error"
                showIcon
                title="预览加载失败或票据已过期"
                description="请关闭后重新点击“查看”。系统不会自动改为下载。"
              />
            )}
            {preview?.ticket.preview_kind === "image" && (
              <img
                className="attachment-image-preview"
                src={preview.ticket.url}
                alt={preview.attachment.display_name}
                onLoad={() => setPreviewLoading(false)}
                onError={failPreviewLoad}
              />
            )}
            {preview?.ticket.preview_kind === "audio" && (
              <audio
                controls
                autoPlay
                src={preview.ticket.url}
                onCanPlay={() => setPreviewLoading(false)}
                onError={failPreviewLoad}
              />
            )}
            {preview?.ticket.preview_kind === "video" && (
              <video
                className="attachment-video-preview"
                controls
                autoPlay
                src={preview.ticket.url}
                onCanPlay={() => setPreviewLoading(false)}
                onError={failPreviewLoad}
              />
            )}
            {preview && ["pdf", "office"].includes(preview.ticket.preview_kind ?? "") && (
              <iframe
                className="attachment-document-preview"
                title={`${preview.attachment.display_name} 在线预览`}
                src={preview.ticket.url}
                onLoad={() => setPreviewLoading(false)}
                onError={failPreviewLoad}
              />
            )}
          </Spin>
        </div>
      </Modal>

      <Modal
        title="重命名附件"
        open={Boolean(renaming)}
        okText="保存"
        cancelText="取消"
        onOk={() => void saveRename()}
        onCancel={() => setRenaming(null)}
      >
        <Typography.Paragraph type="secondary">
          扩展名由系统保留，原始文件名和存储对象键不会变化。
        </Typography.Paragraph>
        <Input
          value={nextName}
          maxLength={255}
          onChange={(event) => setNextName(event.target.value)}
          onPressEnter={() => void saveRename()}
        />
      </Modal>
    </section>
  );
}
